import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa"; // Importing react-icons
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import useRetestPageDetails from "../../utils/hooks/retests/useRetestPageDetails";
import usePageTitle from "../../utils/hooks/usePageTitle";
import LoadingSpinner from "../../components/retests/utils/LoadingSpinner";
import { NoDataAvailable } from "../../components/retests/utils/NoDataAvailable";
import { RetestHeader } from "../../components/retests/LogsPage/RetestHeader";
import { CommentForm } from "../../components/retests/LogsPage/CommentForm";
import { LogList } from "../../components/retests/LogsPage/LogList";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import RetestDetailsRightSection from "../../components/retests/LogsPage/RetestDetailsRightSection";
import { useCreateManualDelegationMutation, useGetObjectDelegationsStatusQuery } from "../../utils/api/endpoints/delegationsApi";
import usePrivateAccessUsers from "../../utils/hooks/user/usePrivateAccessUsers";
import toast from "react-hot-toast";
import { getUserDetailsAdmin } from '../../utils/api/endpoints/user/user';

const RetestPage: React.FC = () => {
  const { retest_id } = useParams<{ retest_id?: string }>();
  const navigate = useNavigate(); // Hook for navigation
  const { role } = useUserCredentials();
  const [refetch, setRefetch] = useState<boolean>(false);
  const [requestedByName, setRequestedByName] = useState<string>("");

  const roleString = role
    ? role === UserRole.RESEARCHER
      ? "RESEARCHER"
      : role === UserRole.BUSINESS
        ? "BUSINESS"
        : role === UserRole.ADMIN
          ? "ADMIN"
          : ""
    : "";


  const { retestDetails: pageDetails, loading, error } = useRetestPageDetails(retest_id, refetch);

  const logs = pageDetails?.logs;
  const reportDetails = pageDetails?.reportDetails;
  const status = pageDetails?.status?.status;
  const createdAt = pageDetails?.status?.createdAt;
  const retestDetails = pageDetails?.rightSection;

  const logsLoading = loading;
  const detailsLoading = loading;
  const statusLoading = loading;
  const retestDetailsLoading = loading;

  const detailsError = error;
  const statusError = error;
  const retestDetailsError = error;

  const [createManualDelegation] = useCreateManualDelegationMutation();
  const isAdminGroup = [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER, UserRole.QA].includes(role as UserRole);

  // Eligible researchers picker (program private access users) - only for admin
  const programId = reportDetails?.program_id;
  const { privateAccessUsers } = usePrivateAccessUsers(isAdminGroup ? programId : undefined);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Delegation status for this retest (admin-only)
  const { data: delegationStatus, refetch: refetchDelegationStatus } = useGetObjectDelegationsStatusQuery(
    { scope: "retest", retest_id: retest_id ? parseInt(retest_id) : undefined },
    { skip: !isAdminGroup || !retest_id }
  );

  const handleSelectDelegate = async (to_user_id: number) => {
    if (!retest_id) return;
    try {
      await (createManualDelegation({ scope: "retest", retest_id: parseInt(retest_id), to_user_id }) as any).unwrap();
      toast.success("Retest delegated");
      setPickerOpen(false);
      refetchDelegationStatus();
    } catch {
      toast.error("Failed to delegate retest");
    }
  };

  usePageTitle(`${reportDetails?.title} - Retest`);

  useEffect(() => {
    if (!logs) return;
    const retestRequestedLog = logs.find((log: any) => log.new_status === "Retest Requested");
    const userId = (retestRequestedLog as any)?.user_id;
    if (retestRequestedLog && userId) {
      getUserDetailsAdmin(userId.toString()).then((user) => {
        setRequestedByName(user.displayName || user.username || "");
      });
    } else if (retestRequestedLog && retestRequestedLog.username) {
      setRequestedByName(retestRequestedLog.username || "");
    } else {
      setRequestedByName("");
    }
  }, [logs]);

  // Loading or Error Handling
  if (logsLoading || detailsLoading || statusLoading || retestDetailsLoading)
    return <LoadingSpinner />;
  if (detailsError || statusError || retestDetailsError)
    return <p>Failed to fetch data...</p>;
  if (!logs || logs.length === 0) return <NoDataAvailable />;

  const researcherId =
    (reportDetails as any)?.userId ||
    (reportDetails as any)?.user_id ||
    (retestDetails as any)?.researcher_user_id ||
    undefined;

  return (
    <div className="montserrat-font container flex min-h-screen max-w-full flex-col gap-4 bg-[#f8f8fc] px-4 py-4">
      {/* Return to Retests */}
      <button
        className="flex items-center gap-2 font-medium hover:underline"
        style={{ color: '#0B45DB' }}
        onClick={() => navigate("/dashboard/retests")}
      >
        <FaArrowLeft className="text-lg" /> {/* Back arrow icon */}
        <span>Return to Retests</span>
      </button>

      {/* Report Name and Program Name */}
      <div className="mb-4 flex flex-col">
        <h1 className="text-2xl font-medium text-gray-900 mb-2">
          {reportDetails?.title || "N/A"}
        </h1>
        <h2 className="text-lg text-[#333333]">
          {reportDetails?.program_creator?.displayName || retestDetails?.program_title || "N/A"}
        </h2>
      </div>

      <RetestHeader
        reportName={reportDetails?.title}
        programName={reportDetails?.program_creator?.displayName}
        severityScore={reportDetails?.severity?.score || reportDetails?.severity}
        severityCategory={reportDetails?.severity?.category}
        retestStatus={status}
        retestDate={createdAt}
      />

      <div className="flex w-full gap-4">
        {/* Left Column - Comments */}
        <div className="w-[70%] flex-grow flex flex-col gap-4">
          <LogList
            logs={logs}
            researcherUserId={researcherId}
            researcherUsername={retestDetails?.researcher_username}
            footer={
              retest_id ? (
                <CommentForm
                  retest_id={retest_id}
                  roleString={roleString}
                  role={role}
                  onCommentSubmit={() => setRefetch(!refetch)}
                  currentStatus={status}
                />
              ) : null
            }
          />
        </div>

        <div className="w-[30%] flex flex-col gap-3">
          {/* Right Column - Details with all props from integration */}
          <RetestDetailsRightSection
            reportTitle={retestDetails?.report_title || reportDetails?.title}
            researcherUsername={retestDetails?.researcher_username}
            researcherPfp={retestDetails?.researcher_pfp}
            businessPfp={retestDetails?.business_pfp}
            targetName={retestDetails?.target_name}
            reportId={reportDetails?.id || (reportDetails as any)?.report_id}
            programName={requestedByName}
            reportScope={reportDetails?.scope}
            programId={reportDetails?.program_id || (reportDetails as any)?.programId}
            severityScore={reportDetails?.severity?.score || reportDetails?.severity}
            category={reportDetails?.category || reportDetails?.asset_type}
            assetName={reportDetails?.asset_name || (reportDetails as any)?.assetName}
            targetValues={reportDetails?.target_values || (reportDetails as any)?.targetValues}
            endpointValues={reportDetails?.endpoint_values || (reportDetails as any)?.endpointValues}
            targetEndpointMap={reportDetails?.target_endpoint_map || (reportDetails as any)?.targetEndpointMap}
            targetMetadataMap={reportDetails?.targetMetadata}
          />

          {isAdminGroup && (
            <>
              {/* Delegation status */}
              <div className="rounded-md border border-gray-200 bg-white p-3 text-sm">
                {(() => {
                  const list = delegationStatus?.data || [];
                  if (list.length === 0) return <span className="text-gray-500">No delegations yet</span>;
                  const latest = list[0];
                  const delegate = privateAccessUsers?.find(u => u.id === latest.to_user_id);
                  const statusColor =
                    latest.status === "accepted"
                      ? "bg-green-100 text-green-800"
                      : latest.status === "pending"
                        ? "bg-yellow-100 text-yellow-800"
                        : latest.status === "rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800";
                  return (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusColor}`}>
                        {latest.status.toUpperCase()}
                      </span>
                      {delegate && (
                        <span>
                          Delegated to {delegate.displayName || `@${(delegate as any).username}`}
                        </span>
                      )}
                      {latest.expires_at && latest.status === "pending" && (
                        <span className="text-gray-500">
                          Expires {new Date(latest.expires_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Delegate action */}
              <div className="relative">
                <button
                  onClick={() => setPickerOpen(v => !v)}
                  className="mt-2 w-full rounded-md bg-ctb-blue-500 px-4 py-2 text-white hover:bg-ctb-blue-600"
                >
                  Delegate Retest
                </button>
                {pickerOpen && (
                  <div className="absolute right-0 z-10 mt-2 max-h-64 w-72 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                    {(() => {
                      // Get list of already delegated users (with accepted status)
                      const delegatedUserIds = (delegationStatus?.data || [])
                        .filter((d: any) => d.status === "accepted")
                        .map((d: any) => d.to_user_id);

                      // Filter researchers: exclude owner and already delegated
                      const eligibleResearchers = (privateAccessUsers || []).filter((u: any) =>
                        u.id !== researcherId && !delegatedUserIds.includes(u.id)
                      );

                      return eligibleResearchers.length > 0 ? (
                        eligibleResearchers.map((u: any) => (
                          <button
                            key={u.id}
                            onClick={() => handleSelectDelegate(u.id)}
                            className="flex w-full items-center gap-2 p-2 text-left hover:bg-gray-50"
                          >
                            {u.pfp && <img src={u.pfp} alt="" className="h-6 w-6 rounded-full" />}
                            <div className="flex flex-col">
                              <span className="text-sm font-medium">{u.displayName || u.username}</span>
                              {u.username && (
                                <span className="text-xs text-gray-500">@{u.username}</span>
                              )}
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="p-3 text-sm text-gray-500">No eligible researchers</div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RetestPage;
